import { useState } from "react";
import { toast } from "sonner";
import { HandHeart, Loader2, CheckCircle2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";

const ROLES = ["Bib Pick-Up", "Water Station", "Snack Station", "Course Directions", "Wherever needed!"];
const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];
const field = "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ink-soft/60 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 transition-colors duration-200";

export default function Volunteer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: ROLES[0], availability: "", shirt_size: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/volunteers", form);
      toast.success(data.message || "Thanks for signing up!");
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Volunteer Sign-up" path="/volunteer" description="Lend a hand at the Grimsby On The Lake Turkey Trot." />
      <PageHeader
        chapter="lend a hand"
        title="Volunteers make the magic."
        sub="From cheering at the finish line to handing out water, our volunteers are the heart of the trot. Join the crew below."
      />

      <section className="mx-auto max-w-5xl px-5 sm:px-8 pb-24 sm:pb-32 grid gap-10 lg:grid-cols-5 items-start">
        <Reveal className="lg:col-span-2">
          <div className="rounded-3xl bg-forest text-cream p-8 sm:p-10">
            <HandHeart size={32} className="text-ochre" />
            <h2 className="mt-5 font-display text-3xl font-semibold">Why volunteer?</h2>
            <ul className="mt-5 space-y-3 text-cream/80 text-sm leading-relaxed">
              <li>• Earn volunteer hours & a free event shirt</li>
              <li>• Be part of an inclusive, joyful community</li>
              <li>• Shifts as short as two hours</li>
              <li>• Coffee, snacks & great company provided</li>
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          {done ? (
            <div className="rounded-3xl bg-white border border-black/5 shadow-warm p-10 text-center" data-testid="volunteer-success">
              <CheckCircle2 size={44} className="mx-auto text-forest" />
              <h3 className="mt-4 font-display text-2xl font-semibold">You're on the team!</h3>
              <p className="mt-2 text-ink-soft">We've received your sign-up and sent race-day details to your inbox. Thank you!</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-white border border-black/5 shadow-warm p-8 sm:p-10 space-y-4" data-testid="volunteer-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" value={form.name} onChange={set("name")} className={field} data-testid="volunteer-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="volunteer-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} data-testid="volunteer-phone" />
                <select value={form.role} onChange={set("role")} className={field} data-testid="volunteer-role">
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Availability (e.g. Sat morning, all day)" value={form.availability} onChange={set("availability")} className={field} data-testid="volunteer-availability" />
                <select required value={form.shirt_size} onChange={set("shirt_size")} className={field} data-testid="volunteer-shirt-size">
                  <option value="" disabled>Shirt size</option>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <textarea placeholder="Anything else we should know? (optional)" rows={4} value={form.message} onChange={set("message")} className={field} data-testid="volunteer-message" />
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-sienna transition-colors duration-200 disabled:opacity-60" data-testid="volunteer-submit">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Sign me up"}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </>
  );
}
